/**
 * The business's logo on this device (docs/08-entscheidungen.md, S13). The data names it only by
 * size and time (it can be large); the picture is fetched once per upload and kept in the browser,
 * so printing has it right in the tap, also without a network.
 *
 * Uploading: whatever the browser can show (PNG, JPG, SVG …) becomes one PNG of at most LOGO_SIDE
 * pixels on its longer side, without the empty edge around it: transparent, or the one colour of
 * its corners (the white around a JPG).
 */
import { useEffect, useSyncExternalStore } from "react";
import { LOGO_SIDE, MAX_LOGO_BYTES, logoOf } from "@speisekarte/core";
import type { Logo } from "@speisekarte/core";
import { api, store, useStore } from "./store.ts";
import type { Data } from "./store.ts";

const key = (workspace: string) => `speisekarte:logo:${workspace}`;
let held: { workspace: string; logo: Logo } | null = null;
let version = 0;
const listeners = new Set<() => void>();
const changed = () => {
  version++;
  for (const fn of listeners) fn();
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => void listeners.delete(fn);
};

/** The logo of the data, when this device has it; null without a logo or before it has come. */
export function heldLogo(data: Data): Logo | null {
  const ref = data.logo;
  const workspace = store.session?.workspaceId ?? "";
  if (!ref) return null;
  if (held?.workspace === workspace && held.logo.at === ref.at) return held.logo;
  try {
    const stored = logoOf(JSON.parse(localStorage.getItem(key(workspace)) ?? "null"));
    if (stored?.at === ref.at) {
      held = { workspace, logo: stored };
      return stored;
    }
  } catch {
    // blocked or broken: fetched again
  }
  return null;
}

/** The logo of the data, fetched when the device does not have it yet. */
export async function loadLogo(data: Data): Promise<Logo | null> {
  const have = heldLogo(data);
  if (have || !data.logo) return have;
  const workspace = store.session?.workspaceId ?? "";
  try {
    const logo = logoOf((await api<{ logo: unknown }>("/api/logo")).logo);
    if (!logo) return null;
    held = { workspace, logo };
    try {
      localStorage.setItem(key(workspace), JSON.stringify(logo));
    } catch {
      // full: kept for this visit only
    }
    changed();
    return logo;
  } catch {
    // Offline: the menu comes without the logo.
    return null;
  }
}

/** Keeps the logo of the business on the device; renders again once it has come. */
export function useLogo(): void {
  const s = useStore();
  useSyncExternalStore(subscribe, () => version);
  const at = s.data?.logo?.at ?? null;
  useEffect(() => {
    if (at !== null && store.data) void loadLogo(store.data);
  }, [at, s.session?.workspaceId, s.online]);
}

/** A picture file as the PNG the server keeps, or why not. */
export async function logoPng(file: File): Promise<{ data: string } | { error: string }> {
  const vector = file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
  const source = vector ? await svgSized(file) : { url: URL.createObjectURL(file), width: 0, height: 0 };
  if (!source) return { error: "Die SVG-Datei lässt sich nicht lesen." };
  try {
    const img = new Image();
    img.src = source.url;
    try {
      await img.decode();
    } catch {
      return { error: "Das Bild lässt sich nicht lesen. Bitte eine PNG-, JPG- oder SVG-Datei nehmen." };
    }
    const w = source.width || img.naturalWidth;
    const h = source.height || img.naturalHeight;
    if (!w || !h) return { error: "Das Bild hat keine Größe." };
    // A drawing comes at its size already (svgSized); a photo never larger than it is, and before
    // its edge goes with room for it (four times LOGO_SIDE), so what is left still has LOGO_SIDE.
    const scale = vector ? 1 : Math.min(1, (4 * LOGO_SIDE) / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const box = contentBox(ctx, canvas.width, canvas.height);
    if (!box) return { error: "Das Bild ist leer." };
    let side = vector ? 1 : Math.min(1, LOGO_SIDE / Math.max(box.w, box.h));
    const least = side * 0.3;
    for (;;) {
      const out = document.createElement("canvas");
      out.width = Math.max(1, Math.round(box.w * side));
      out.height = Math.max(1, Math.round(box.h * side));
      out.getContext("2d")!.drawImage(canvas, box.x, box.y, box.w, box.h, 0, 0, out.width, out.height);
      const blob = await new Promise<Blob | null>((r) => out.toBlob(r, "image/png"));
      if (!blob) return { error: "Das Bild lässt sich nicht umwandeln." };
      if (blob.size <= MAX_LOGO_BYTES) return { data: await base64(blob) };
      // Too large (a photo): smaller, down to about a third.
      side *= 0.8;
      if (side < least) return { error: "Das Logo ist zu groß. Bitte eine kleinere Datei oder eine Grafik (PNG, SVG) nehmen." };
    }
  } finally {
    URL.revokeObjectURL(source.url);
  }
}

/**
 * An SVG with its size written on it, LOGO_SIDE on the longer side: browsers give a drawing that
 * has only a viewBox a size of their own (300 × 150), which would bend it.
 */
async function svgSized(file: File): Promise<{ url: string; width: number; height: number } | null> {
  const text = await file.text();
  const tag = /<svg\b[^>]*>/i.exec(text)?.[0];
  if (!tag) return null;
  const box = /\sviewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)\s*["']/i.exec(tag);
  const attr = (name: string) => Number(new RegExp(`\\s${name}\\s*=\\s*["']\\s*([\\d.]+)(px)?\\s*["']`, "i").exec(tag)?.[1] ?? NaN);
  let [w, h] = [attr("width"), attr("height")];
  if (!(w > 0 && h > 0)) [w, h] = box ? [Number(box[1]), Number(box[2])] : [300, 150];
  if (!(w > 0 && h > 0)) return null;
  const scale = LOGO_SIDE / Math.max(w, h);
  const [width, height] = [Math.round(w * scale), Math.round(h * scale)];
  let root = tag.replace(/\s(width|height)\s*=\s*(["'])[^"']*\2/gi, "");
  if (!box) root = root.replace(/^<svg/i, `<svg viewBox="0 0 ${w} ${h}"`);
  root = root.replace(/^<svg/i, `<svg width="${width}" height="${height}"`);
  return { url: URL.createObjectURL(new Blob([text.replace(tag, root)], { type: "image/svg+xml" })), width, height };
}

/** How far a pixel's colour may stray from the background and still count as background: room for JPEG's noise. */
const EDGE_TOLERANCE = 24;
/** The least of each channel for a background that may go: white and off-white paper. */
const LIGHT = 225;

/**
 * The part of the picture that is not its empty edge; null: nothing to see. Empty is transparent,
 * and in a picture without transparency (a JPG, a PNG on white) also the light colour its four
 * corners share, so the white around a logo goes as well. Only a light one: cut away on white paper
 * it was never seen, while a coloured badge that fills the picture stays whole.
 */
function contentBox(ctx: CanvasRenderingContext2D, w: number, h: number): { x: number; y: number; w: number; h: number } | null {
  const px = ctx.getImageData(0, 0, w, h).data;
  const at = (x: number, y: number) => (y * w + x) * 4;
  const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
  const near = (i: number, j: number) => Math.abs(px[i]! - px[j]!) <= EDGE_TOLERANCE && Math.abs(px[i + 1]! - px[j + 1]!) <= EDGE_TOLERANCE && Math.abs(px[i + 2]! - px[j + 2]!) <= EDGE_TOLERANCE;
  // A background only when the corners are opaque, light and of one colour; the first corner stands for it.
  const light = (i: number) => px[i]! >= LIGHT && px[i + 1]! >= LIGHT && px[i + 2]! >= LIGHT;
  const ground = corners.every((c) => px[c + 3]! >= 250 && light(c) && near(c, corners[0]!)) ? corners[0]! : null;
  let [left, top, right, bottom] = [w, h, -1, -1];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = at(x, y);
      if (px[i + 3]! < 8 || (ground !== null && near(i, ground))) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  return right < 0 ? null : { x: left, y: top, w: right - left + 1, h: bottom - top + 1 };
}

const base64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
